import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fijarAccessToken } from '../../lib/api.ts';
import { CLAVE_SESION } from '../../lib/sesion.ts';
import { Plus } from 'lucide-react';
import { Aviso, Avatar, Boton, Campo, EncabezadoPagina, Insignia, Tarjeta } from '../../componentes/ui.tsx';

export interface EmpresaSuper {
  id: string;
  slug: string;
  nombre: string;
  pais: string;
  estado: 'ACTIVO' | 'INACTIVO';
  fechaExpiracion: string | null;
  vencida: boolean;
  clientes: number;
  productos: number;
  usuarios: number;
  postsUltimos30: number;
}

interface Salud {
  db: boolean;
  renderer: boolean;
  colas: Record<string, { esperando: number; activos: number; fallidos: number }>;
  empresas: number;
  usuarios: number;
  postsHoy: number;
}

/**
 * Impersonación auditada (§8.1): entra como el primer ADMIN_EMPRESA de la empresa.
 * El servidor cambia la cookie de sesión por la del impersonado y deja una cookie de
 * retorno del superadmin; el reload ya aterriza DENTRO de la cuenta.
 */
export async function impersonarEmpresa(
  empresaId: string,
  _invalidar: () => Promise<void>,
): Promise<string | null> {
  const { items } = await api<{ items: { id: string; rol: string }[] }>(
    `/super/empresas/${empresaId}/usuarios`,
  );
  const admin = items.find((u) => u.rol === 'ADMIN_EMPRESA') ?? items[0];
  if (!admin) return 'La empresa no tiene usuarios';
  const r = await api<{ accessToken: string }>(`/super/impersonar/${admin.id}`, { metodo: 'POST' });
  fijarAccessToken(r.accessToken);
  window.location.href = '/';
  return null;
}

export function PaginaSuperadmin() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [slug, setSlug] = useState('');
  const [nombre, setNombre] = useState('');
  const [ruc, setRuc] = useState('');
  const [pais, setPais] = useState('CO');
  const [email, setEmail] = useState('');
  const [expiraNueva, setExpiraNueva] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [mostrarAlta, setMostrarAlta] = useState(false);

  const empresas = useQuery({
    queryKey: ['super-empresas'],
    queryFn: () => api<{ items: EmpresaSuper[] }>('/super/empresas'),
  });
  const salud = useQuery({
    queryKey: ['super-salud'],
    queryFn: () => api<Salud>('/super/salud'),
    refetchInterval: 15_000,
  });

  const recargar = () => qc.invalidateQueries({ queryKey: ['super-empresas'] });

  const crear = useMutation({
    mutationFn: () =>
      api<{ id: string; credencial: { username: string; password: string } }>('/super/empresas', {
        metodo: 'POST',
        cuerpo: {
          slug: slug.trim(),
          codigoExterno: slug.trim().toUpperCase(),
          nombre: nombre.trim(),
          pais,
          adminEmail: email.trim(),
          ...(ruc.trim() ? { ruc: ruc.trim() } : {}),
          // Sin fecha = cuenta indefinida.
          ...(expiraNueva ? { fechaExpiracion: new Date(`${expiraNueva}T23:59:59`).toISOString() } : {}),
        },
      }),
    onSuccess: async (r) => {
      setAviso(
        `Empresa creada. Admin ${r.credencial.username} · clave ${r.credencial.password} — anótela ahora, no se vuelve a mostrar.`,
      );
      setSlug(''); setNombre(''); setRuc(''); setEmail(''); setExpiraNueva('');
      await recargar();
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const editar = useMutation({
    mutationFn: ({ id, cuerpo }: { id: string; cuerpo: Record<string, unknown> }) =>
      api(`/super/empresas/${id}`, { metodo: 'PATCH', cuerpo }),
    onSuccess: () => recargar(),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const eliminar = useMutation({
    mutationFn: ({ id, confirmarSlug }: { id: string; confirmarSlug: string }) =>
      api(`/super/empresas/${id}`, { metodo: 'DELETE', cuerpo: { confirmarSlug } }),
    onSuccess: async () => {
      setAviso('Empresa eliminada con todos sus datos.');
      await recargar();
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  function borrarEmpresa(e: EmpresaSuper) {
    const escrito = window.prompt(
      `Esto BORRA "${e.nombre}" con TODOS sus datos. No hay vuelta atrás.\n` +
        `Para suspender sin perder nada use Desactivar o la expiración.\n\n` +
        `Escriba el slug exacto para confirmar: ${e.slug}`,
    );
    if (escrito === null) return;
    eliminar.mutate({ id: e.id, confirmarSlug: escrito });
  }

  const accion =
    'inline-flex min-h-9 items-center rounded-lg bg-superficie2 px-2.5 text-xs font-semibold text-principal hover:bg-superficie2';

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo="Plataforma"
        subtitulo={`${empresas.data?.items.length ?? 0} empresas`}
        acciones={
          <Boton variante="contorno" onClick={() => setMostrarAlta((v) => !v)}>
            <Plus size={16} /> Nueva empresa
          </Boton>
        }
      />
      {aviso ? <Aviso tono={aviso.startsWith('Error') ? 'error' : 'ok'}>{aviso}</Aviso> : null}

      {salud.data ? (
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          {(
            [
              ['Base de datos', salud.data.db ? 'OK' : 'CAÍDA', salud.data.db],
              ['Renderer', salud.data.renderer ? 'OK' : 'CAÍDO', salud.data.renderer],
              ['Empresas', salud.data.empresas, true],
              ['Usuarios', salud.data.usuarios, true],
              ['Posts hoy', salud.data.postsHoy, true],
            ] as const
          ).map(([etiqueta, valor, ok], i) => (
            <div key={String(etiqueta)} className={`${i === 0 ? 'tarjeta-negra' : 'tarjeta'} p-5`}>
              <p className={`text-xs ${i === 0 ? 'opacity-60' : 'text-suave'}`}>{etiqueta}</p>
              <p className={`mt-1 text-[32px] font-light leading-none ${ok ? '' : 'text-rose-500'}`}>{valor}</p>
            </div>
          ))}
        </section>
      ) : null}

      {/* Alta */}
      <details className="tarjeta" open={mostrarAlta}>
        <summary className="cursor-pointer list-none px-5 py-4 text-[15px] font-medium">Nueva empresa</summary>
        <form
          className="grid gap-3 border-t border-superficie2 p-5 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            crear.mutate();
          }}
        >
          <Campo etiqueta="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} required />
          <Campo etiqueta="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          <Campo etiqueta="RUC / NIT" value={ruc} onChange={(e) => setRuc(e.target.value)} />
          <Campo etiqueta="País (ISO-2)" value={pais} onChange={(e) => setPais(e.target.value)} required maxLength={2} />
          <Campo etiqueta="Email del admin" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <div>
            <Campo etiqueta="Expiración" type="date" value={expiraNueva} onChange={(e) => setExpiraNueva(e.target.value)} />
            <span className="mt-1 block text-xs text-suave">Vacío = sin expiración (indefinida)</span>
          </div>
          <div className="flex items-end">
            <Boton type="submit" disabled={crear.isPending}>Crear empresa</Boton>
          </div>
        </form>
      </details>

      {/* Empresas en filas con quick actions */}
      <Tarjeta className="overflow-x-auto !p-2">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-suave">
            <tr>
              <th className="px-4 py-3">Empresa</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Expira</th>
              <th className="px-4 py-3">Clientes</th>
              <th className="px-4 py-3">Posts/30d</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-superficie2">
            {empresas.data?.items.map((e) => {
              const operativa = e.estado === 'ACTIVO' && !e.vencida;
              return (
                <tr key={e.id} className="rounded-2xl hover:bg-superficie2">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar nombre={e.nombre} />
                      <div>
                        <Link
                          to="/super/empresas/$empresaId"
                          params={{ empresaId: e.id }}
                          className="text-[15px] font-medium hover:underline"
                        >
                          {e.nombre}
                        </Link>
                        <p className="text-xs text-suave">{e.slug} · {e.pais}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Insignia tono={operativa ? 'ok' : 'alerta'}>
                      {e.estado === 'INACTIVO' ? 'DESACTIVADA' : e.vencida ? 'VENCIDA' : 'ACTIVA'}
                    </Insignia>
                  </td>
                  <td className="px-4 py-3 text-xs text-suave">
                    {e.fechaExpiracion ? e.fechaExpiracion.slice(0, 10) : 'indefinida'}
                  </td>
                  <td className="px-4 py-3">{e.clientes}</td>
                  <td className="px-4 py-3">{e.postsUltimos30}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        className={accion}
                        onClick={() =>
                          void navegar({ to: '/super/empresas/$empresaId', params: { empresaId: e.id } })
                        }
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className={accion}
                        onClick={() =>
                          void impersonarEmpresa(e.id, () =>
                            qc.invalidateQueries({ queryKey: CLAVE_SESION }),
                          ).then((err) => err && setAviso(`Error: ${err}`))
                        }
                      >
                        Entrar
                      </button>
                      <button
                        type="button"
                        className={accion}
                        onClick={() =>
                          editar.mutate({
                            id: e.id,
                            cuerpo: { estado: e.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' },
                          })
                        }
                      >
                        {e.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        type="button"
                        className={`${accion} border-rose-200 text-rose-600 hover:bg-rose-50`}
                        onClick={() => borrarEmpresa(e)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Tarjeta>
    </main>
  );
}
